import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import WidgetErrorMessage from '../WidgetErrorMessage';
import AirportStatusMap from './AirportStatusMap';
import {
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourcesForRequests,
    getMetricSourceForRequest,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../utils/wrapperUtils';
import DashboardContext from '../../../shared/hooks/dashboardContext';

/* eslint-disable complexity */
function getMetricSources({ metricRequests, metrics }) {
    if (metricRequests.length === 0) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const metricSources = getMetricSourcesForRequests(metrics, metricRequests);
    if (metricSources.some((metricSource) => !metricSource)) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized(metricSources)) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources(metricSources, false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        metricSources,
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSources, metricRequests };
}

export function hydrateButtonLinks(button, openLinksInNewTab, getNonApiUri) {
    if (!button.path) {
        return button;
    }

    return {
        ...button,
        target: openLinksInNewTab ? '_blank' : '',
        uri: button.isAbsoluteUri ? button.path : getNonApiUri(button.path),
    };
}
/* eslint-enable complexity */

export function getButtonDataFromMetricSource(
    button,
    metricSources,
    metricRequests
) {
    const metricRequest = metricRequests.find(
        (request) => request.id === button.metricRequestId
    );

    const metricSource = getMetricSourceForRequest(
        metricSources,
        metricRequest
    );

    return {
        ...button,
        statusValue: metricSource?.metricSeries?.[0]?.[button.metric],
    };
}

export function getButtonConfig(filterValues, basedOnFilterKey, buttonConfigs) {
    if (!buttonConfigs) {
        return null;
    }

    if (!basedOnFilterKey) {
        return buttonConfigs[0];
    }

    const filterValue = filterValues[basedOnFilterKey]?.values?.[0]?.valueField;
    return buttonConfigs.find(
        (x) => x.matchFilterValue.toLowerCase() === filterValue.toLowerCase()
    );
}

// eslint-disable-next-line complexity
export default function AirportStatusMapWrapper(props) {
    const {
        uriConstants,
        assets: { statusMapImages },
    } = useContext(DashboardContext);

    const { metricSources, metricRequests, metricSourceErrors, errorKey } =
        getMetricSources(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const buttonConfig = getButtonConfig(
        metricRequests[0].filters,
        props.configuration.basedOnFilterKey,
        props.configuration.buttonConfigs
    );

    if (!buttonConfig) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels.airportMapConfigMissing}
            </WidgetErrorMessage>
        );
    }

    const buttonsWithFullUri = buttonConfig.buttons.map((button) =>
        hydrateButtonLinks(
            button,
            props.openLinksInNewTab,
            uriConstants.getNonApiUri
        )
    );

    const buttonsWithData = buttonsWithFullUri.map((button) =>
        getButtonDataFromMetricSource(button, metricSources, metricRequests)
    );

    return (
        <div className="chart-container">
            <AirportStatusMap
                {...props}
                statusMapImage={statusMapImages[buttonConfig.statusMapAsset]}
                buttons={buttonsWithData}
            />
        </div>
    );
}

AirportStatusMapWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
};
